import { useEffect, useMemo } from 'react';
import { usePageData } from 'lib/page-data/PageDataContext';
import {
  getAssetClassName,
  getSelectedShareClass,
  getSubAssetClassName,
} from '@/store/fundDetails';
import { useSelector } from 'react-redux';
import { useSitecoreContext } from './useSitecoreContext';
import { BreadcrumbsProps } from 'model/Breadcrumbs';
import findComponentInTree from 'lib/utils/findComponentInTree';
import { fundDetailsDataFetcher } from 'lib/wildcards/dataFetchers/FundDetailsDataFetcher';
import { getReferrer } from '@/store/global';
import { pushDataLayerEventAfterOneTrust } from './dataLayerPush';
import { SitecoreContextValue } from '@sitecore-jss/sitecore-jss-nextjs';
import { SitecoreContextDataLayer } from 'model/DataLayer';
import { ShareClasses } from 'model/FundDetails';

type PropositionType = {
  investment_fund: {
    fund_code: string;
  };
};

type ProductTags = {
  assetClasses?: string | Array<string>;
  subAssetClasses?: string | Array<string>;
  capabilities?: string;
  capabilityreference?: string[];
};

type PageViewEvent = Partial<SitecoreContextDataLayer> & {
  event: string;
  page_title: string | undefined;
  page_location: string | undefined;
  breadcrumbs: Array<string> | string;
  proposition: PropositionType | undefined;
  product_tags: ProductTags | undefined;
  global: any;
  page_referrer?: string;
  page_path?: string;
};

const replaceNullValues = (data: PageViewEvent) => {
  const replaceNull = (value: any): any => {
    if (value === null || value === '') {
      return 'n/a';
    } else if (Array.isArray(value)) {
      return value.map(replaceNull);
    } else if (typeof value === 'object' && value !== null) {
      const newObj: Record<string, any> = {};
      for (const key in value) {
        newObj[key] = replaceNull(value[key]);
      }
      return newObj;
    } else {
      return value;
    }
  };

  return replaceNull(data) as PageViewEvent;
};

const getWindow = () => (typeof window !== 'undefined' ? window : undefined);
const createPageViewEvent = ({
  sitecoreContext,
  breadcrumbs,
  referrer,
  selectedShareClass,
  assetClassName,
  subAssetClassName,
  pageTitle,
}: {
  sitecoreContext: SitecoreContextValue;
  breadcrumbs: string[] | 'n/a';
  referrer?: string;
  selectedShareClass?: ShareClasses;
  assetClassName?: string;
  subAssetClassName?: string;
  pageTitle?: string;
}): PageViewEvent => {
  const [language, country] = sitecoreContext?.language?.split('-') ?? [];
  const capabilityReference =
    sitecoreContext.route.fields?.pageFundCapabilities?.[0]?.fields.CapabilityReference;
  return {
    event: 'page_view',
    page_title: sitecoreContext.metaTitle ?? pageTitle,
    breadcrumbs: breadcrumbs,
    page_location: getWindow()?.location.href ?? '',
    page_referrer: referrer,
    page_path: getWindow()?.location.pathname,
    global: {
      ...sitecoreContext.dataLayer?.global,
      country,
      language,
    },
    proposition: {
      investment_fund: {
        fund_code: selectedShareClass?.isin ?? 'n/a',
      },
    },
    product_tags: selectedShareClass
      ? {
          assetClasses: assetClassName,
          subAssetClasses: subAssetClassName,
        }
      : {
          assetClasses: sitecoreContext.route.fields?.pageFundAssetClasses
            ?.map((item) => item.fields.className.value)
            .join('|'),
          subAssetClasses: sitecoreContext.route.fields?.pageFundSubAssetClasses
            ?.map((item) => item.fields.name.value)
            .join('|'),
          capabilities: sitecoreContext.route.fields?.pageFundCapabilities
            ?.map((item) => item.fields.name.value)
            .join('|'),
          capabilityreference: capabilityReference?.[0]?.fields?.CapabilityCode.value,
        },
    ...sitecoreContext.dataLayer,
  };
};

// See: https://developer.onetrust.com/onetrust/docs/single-page-applications
const initOneTrust = () => {
  window.OneTrust?.initializeCookiePolicyHtml?.();
  const cookieButtons = document.getElementsByClassName(
    'ot-sdk-show-settings'
  ) as HTMLCollectionOf<HTMLElement>;
  for (const cookieButton of cookieButtons) {
    cookieButton.onclick = function (event) {
      event.stopImmediatePropagation();
      window.OneTrust?.ToggleInfoDisplay();
    };
  }
};

export const useOnPageLoad = () => {
  const { sitecoreContext } = useSitecoreContext();
  const pageTitle: string | undefined = usePageData('pageTitle');
  const breadcrumbComponent = findComponentInTree<BreadcrumbsProps['fields']>(
    sitecoreContext.route?.placeholders['abrdn-header'],
    'Breadcrumbs'
  );
  const breadcrumbs =
    breadcrumbComponent?.fields?.breadcrumbs.value.map(({ name }) => name) || 'n/a';
  const selectedShareClass = useSelector(getSelectedShareClass);
  const assetClassName = useSelector(getAssetClassName);
  const subAssetClassName = useSelector(getSubAssetClassName);
  const referrer = useSelector(getReferrer);

  const pageViewEvent: PageViewEvent = useMemo(
    () =>
      createPageViewEvent({
        sitecoreContext,
        breadcrumbs,
        referrer,
        selectedShareClass,
        assetClassName,
        subAssetClassName,
        pageTitle,
      }),
    [
      assetClassName,
      breadcrumbs,
      pageTitle,
      referrer,
      selectedShareClass,
      sitecoreContext,
      subAssetClassName,
    ]
  );

  useEffect(() => {
    const templateId = sitecoreContext.route?.templateId;
    if (templateId && !fundDetailsDataFetcher.templateIds.includes(templateId?.toUpperCase())) {
      // handle all the pages except fundDetails
      pushDataLayerEventAfterOneTrust(replaceNullValues(pageViewEvent));
    }
    initOneTrust();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitecoreContext]);

  useEffect(() => {
    if (selectedShareClass) {
      // handle fundDetails page data specifically
      pushDataLayerEventAfterOneTrust(replaceNullValues(pageViewEvent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShareClass]);
};
